import { Box, Typography, Paper, Stack } from '@mui/material';
import dayjs from 'dayjs';
import { useDayTimeline } from './useDayTimeline';
import TimeSlotRow from './TimeSlotRow';
import VisitBlock from './VisitBlock';
import HolidayBlock from './HolidayBlock';
import type { DayTimelineViewProps } from './entity';
import { useWorkingHours } from './useWorkingHours';
import { useVisitsQuery } from '../../queries';
import { useWorkScheduleQuery } from '../../../profile/work-schedule/hooks';

type DayTimelineProps = DayTimeline & {
  onSlotClick?: (time: string) => void;
  onVisitClick?: (visitId: string) => void;
};

type DayTimeline = {
  date: Date;
  previewVisit?: DayTimelineViewProps['previewVisit'];
};

const useDayTimelineViewData = ({ date, previewVisit }: DayTimeline) => {
  const workingHours = useWorkingHours(date);
  const targetDate = dayjs(date);
  const { data: workScheduleData } = useWorkScheduleQuery(
    targetDate.year(),
    targetDate.month() + 1,
  );
  const { data: visitsForMonth } = useVisitsQuery({
    query: {
      from: dayjs(date).startOf('month'),
      to: dayjs(date).endOf('month'),
    },
  });

  const { timeSlots, visitBlocks, previewBlock, holidayBlocks, slotHeight } = useDayTimeline(
    date,
    visitsForMonth,
    workingHours,
    previewVisit,
    workScheduleData?.schedule,
  );

  return {
    date,
    timeSlots,
    visitBlocks,
    previewBlock,
    holidayBlocks,
    slotHeight,
    workingHours,
  };
};

const DayTimelineView = ({ date, previewVisit, onSlotClick, onVisitClick }: DayTimelineProps) => {
  const { timeSlots, visitBlocks, previewBlock, holidayBlocks, slotHeight, workingHours } =
    useDayTimelineViewData({ date, previewVisit });

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <Box
        sx={{
          padding: 2,
          borderBottom: '2px solid',
          borderColor: 'primary.main',
          backgroundColor: 'primary.light',
        }}
      >
        <Typography variant="h6" color="primary.main" fontWeight="bold">
          {dayjs(date).format('dddd, D. MMMM YYYY')}
        </Typography>
      </Box>

      {/* Timeline Content */}
      <Box sx={{ position: 'relative', overflowY: 'auto', maxHeight: '70vh', width: '100%' }}>
        <Stack spacing={0}>
          {timeSlots.map(slot => (
            <TimeSlotRow
              key={slot.time}
              slot={slot}
              height={slotHeight}
              onClick={time =>
                onSlotClick?.(
                  dayjs(date)
                    .hour(parseInt(time.split(':')[0]))
                    .minute(parseInt(time.split(':')[1]))
                    .toISOString(),
                )
              }
              isHalfHour={slot.time.endsWith(':30')}
            />
          ))}
        </Stack>

        {/* Visits Layer */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 46,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            paddingRight: 1,
          }}
        >
          {visitBlocks.map(visit => {
            const column = visit.column || 0;
            const totalColumns = visit.totalColumns || 1;
            const widthPercent = 100 / totalColumns;
            const leftPercent = column * widthPercent;

            return (
              <Box
                key={visit.id}
                sx={{
                  position: 'absolute',
                  pointerEvents: 'auto',
                  left: `${leftPercent}%`,
                  top: 0,
                  width: `${widthPercent}%`,
                  paddingRight: 0.5,
                }}
              >
                <VisitBlock visit={visit} onClick={onVisitClick} />
              </Box>
            );
          })}

          {/* Holiday Blocks */}
          {holidayBlocks.map(holiday => (
            <Box
              key={holiday.id}
              sx={{
                position: 'absolute',
                pointerEvents: 'auto',
                left: 0,
                top: 0,
                width: '100%',
                paddingRight: 1,
              }}
            >
              <HolidayBlock holiday={holiday} />
            </Box>
          ))}

          {/* Preview Visit */}
          {previewBlock && (
            <Box
              sx={{
                position: 'absolute',
                pointerEvents: 'none',
                left: 0,
                top: 0,
                width: '100%',
                paddingRight: 1,
              }}
            >
              <VisitBlock visit={previewBlock} isPreview />
            </Box>
          )}
        </Box>

        {/* Current Time Indicator */}
        {dayjs(date).isSame(dayjs(), 'day') && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: `${((dayjs().hour() * 60 + dayjs().minute() - (workingHours?.start ? parseInt(workingHours.start.split(':')[0]) * 60 : 8 * 60)) / 30) * slotHeight}px`,
              height: 2,
              backgroundColor: 'error.main',
              zIndex: 100,
              pointerEvents: 'none',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: -4,
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: 'error.main',
              },
            }}
          />
        )}
      </Box>
    </Paper>
  );
};

export default DayTimelineView;
